import { useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useRippWallet, mediaUrl } from "./wallet";

export const TIER_COLOR = { COMMON: "#9AA4B2", UNCOMMON: "#38BDF8", RARE: "#2563EB", EPIC: "#D946EF", LEGENDARY: "#F5C451", GENESIS: "#F5C451" };

export function RippLayout({ children, wide }) {
  const w = useRippWallet();
  const short = w.wallet ? `${w.wallet.slice(0, 4)}…${w.wallet.slice(-4)}` : null;
  return (
    <div className="min-h-screen bg-[#070707] text-[#F3F1EA] font-['Manrope',_'Space_Grotesk',_sans-serif]" style={{ background: "radial-gradient(1200px 600px at 20% -10%, #1b1608 0%, #070707 60%)" }}>
      <header className="flex items-center justify-between px-6 lg:px-12 py-5 border-b border-white/5" data-testid="ripp-nav">
        <Link to="/ripp" className="flex items-baseline gap-3"><span className="text-2xl font-black tracking-[0.35em] text-[#F5C451]">RIPP</span><span className="text-[10px] tracking-[0.3em] text-white/40">GENESIS COLLECTION</span></Link>
        <nav className="hidden md:flex gap-7 text-xs tracking-[0.25em] uppercase text-white/50">
          {[["/ripp/collection", "Collection"], ["/ripp/mint", "Mint"], ["/ripp/token", "$RIPP"], ["/ripp/lore", "Lore"], ["/ripp/engine", "Engine"], ["/ripp/roadmap", "Roadmap"], ["/ripp/my-ripps", "My RIPPs"], ["/ripp/holder", "Holder"]].map(([to, l]) => (
            <NavLink key={to} to={to} className={({ isActive }) => `hover:text-[#F5C451] transition-colors ${isActive ? "text-[#F5C451]" : ""}`} data-testid={l === "$RIPP" ? "nav-token" : `nav-${l.toLowerCase().replace(/\s/g, "-")}`}>{l}</NavLink>))}
        </nav>
        <div className="flex items-center gap-2">
          {w.session && short ? (
            <button onClick={w.signOut} className="px-4 py-2 rounded-full border border-[#F5C451]/40 text-[#F5C451] text-xs tracking-widest" data-testid="wallet-signout-btn">{short} · SIGNED IN</button>
          ) : (
            <button onClick={w.signIn} disabled={w.busy} className="px-4 py-2 rounded-full bg-[#F5C451] text-black text-xs font-bold tracking-widest hover:bg-[#ffd76a] transition-colors disabled:opacity-50" data-testid="wallet-signin-btn">
              {w.busy ? "SIGNING…" : w.wallet ? `SIGN IN · ${short}` : "CONNECT WALLET"}</button>
          )}
        </div>
      </header>
      {w.error && <div className="mx-6 lg:mx-12 mt-4 text-xs text-red-300 border border-red-400/30 rounded-lg px-3 py-2" data-testid="wallet-error">{w.error}</div>}
      <main className={`px-6 lg:px-12 py-10 ${wide ? "" : "max-w-7xl mx-auto"}`}>{children}</main>
      <footer className="px-6 lg:px-12 py-8 text-[11px] text-white/30 border-t border-white/5" data-testid="ripp-disclaimer">
        RIPP NFTs are digital collectibles and membership credentials for the RIPP platform. Holding one unlocks software features only — no trading profits, investment returns or token appreciation are promised.
      </footer>
    </div>
  );
}

export function NftMedia({ item, size = "display_1024", className = "", play = true }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const reduced = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.2 });
    io.observe(el); return () => io.disconnect();
  }, []);
  useEffect(() => { const v = ref.current?.querySelector("video"); if (!v) return; if (visible && play && !reduced) v.play().catch(() => {}); else v.pause(); }, [visible, play, reduced]);
  const m = item?.media || {};
  const still = mediaUrl(size === "thumb" ? (m.thumbnail_uri || m.thumbnail_local) : (m.display_1024_uri || m.display_image_uri || m.preview_uri));
  const anim = mediaUrl(m.animation_uri);
  return (
    <div ref={ref} className={`relative overflow-hidden bg-[#0d0d0d] ${className}`} data-testid="nft-media">
      {still ? <img src={still} alt={item?.name} loading="lazy" className="w-full h-full object-cover" /> : (
        <div className="w-full h-full min-h-[160px] flex items-center justify-center text-white/30 text-xs tracking-widest">{item?.state === "DRAFT" ? "NOT RENDERED" : "NO MEDIA"}</div>)}
      {anim && visible && !reduced && <video src={anim} poster={still} muted loop playsInline preload="none" className="absolute inset-0 w-full h-full object-cover" />}
    </div>
  );
}

export function TierBadge({ tier, genesis }) {
  const t = genesis ? "GENESIS" : tier;
  return <span className="text-[10px] tracking-[0.25em] px-2 py-1 rounded-full border" style={{ color: TIER_COLOR[t] || "#ccc", borderColor: `${TIER_COLOR[t] || "#ccc"}55` }} data-testid="tier-badge">{t || "—"}</span>;
}

export function NftCard({ item }) {
  return (
    <Link to={`/ripp/nft/${item.number}`} className="group block rounded-2xl overflow-hidden border border-white/5 bg-[#0b0b0b] hover:border-[#F5C451]/40 transition-colors" data-testid={`nft-card-${item.number}`}>
      <NftMedia item={item} size="thumb" className="aspect-square" play={false} />
      <div className="p-4 flex items-center justify-between">
        <div><div className="text-sm font-semibold">{item.name}</div><div className="text-[11px] text-white/40 tracking-widest uppercase">{item.character_name}</div></div>
        <TierBadge tier={item.rarity_tier} genesis={item.genesis} />
      </div>
    </Link>
  );
}
